import '@diekeure/dcr-components/dcr-switch.js';

import { createSelect, MountTarget, newDialog, readConfig } from './utils/elements';

export const TIP_EDIT_SELECTOR = '.cds-lrn-tip';

type TipTemplate = 'inline' | 'titled-corner' | 'titled';

interface TipConfig {
  template: TipTemplate;
  banner: boolean;
  icon: string;
}

interface TipContent {
  title: string; // HTML
  text: string; // HTML
}

const TITLE_PLACEHOLDER = 'Titel';
const TEXT_PLACEHOLDER = 'Tekst';

const TEMPLATE_THUMBS: { label: string; value: TipTemplate; svg: string; svgBanner: string }[] = [
  {
    label: 'Inline',
    value: 'inline',
    svg: `<svg viewBox="0 0 80 40" width="80" height="40">
      <rect x="3" y="6" width="74" height="28" rx="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
      <circle cx="13" cy="20" r="5" fill="currentColor"/>
      <line x1="23" y1="16" x2="70" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <line x1="23" y1="24" x2="58" y2="24" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    svgBanner: `<svg viewBox="0 0 80 40" width="80" height="40">
      <defs><clipPath id="tip-thumb-inline-b"><rect x="3" y="6" width="74" height="28" rx="4"/></clipPath></defs>
      <g clip-path="url(#tip-thumb-inline-b)"><rect x="3" y="6" width="74" height="28" fill="currentColor"/></g>
      <rect x="3" y="6" width="74" height="28" rx="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
      <circle cx="13" cy="20" r="5" fill="white"/>
      <line x1="23" y1="20" x2="68" y2="20" stroke="white" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
  },
  {
    label: 'Titel + lampje hoek',
    value: 'titled-corner',
    svg: `<svg viewBox="0 0 80 50" width="80" height="50">
      <rect x="7" y="12" width="66" height="34" rx="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
      <line x1="14" y1="22" x2="48" y2="22" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
      <line x1="14" y1="32" x2="66" y2="32" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="14" y1="39" x2="58" y2="39" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <circle cx="9" cy="14" r="6" fill="currentColor"/>
    </svg>`,
    svgBanner: `<svg viewBox="0 0 80 50" width="80" height="50">
      <defs><clipPath id="tip-thumb-titled-corner-b"><rect x="7" y="12" width="66" height="34" rx="4"/></clipPath></defs>
      <g clip-path="url(#tip-thumb-titled-corner-b)"><rect x="7" y="12" width="66" height="14" fill="currentColor"/></g>
      <rect x="7" y="12" width="66" height="34" rx="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
      <rect x="18" y="17" width="32" height="3" rx="1.5" fill="white"/>
      <line x1="14" y1="33" x2="64" y2="33" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="14" y1="40" x2="58" y2="40" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <circle cx="9" cy="14" r="6" fill="currentColor" stroke="white" stroke-width="1.5"/>
    </svg>`,
  },
  {
    label: 'Titel + tekst',
    value: 'titled',
    svg: `<svg viewBox="0 0 80 50" width="80" height="50">
      <rect x="7" y="8" width="66" height="34" rx="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
      <circle cx="14" cy="18" r="4" fill="currentColor"/>
      <line x1="22" y1="18" x2="50" y2="18" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
      <line x1="14" y1="28" x2="66" y2="28" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="14" y1="35" x2="58" y2="35" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`,
    svgBanner: `<svg viewBox="0 0 80 50" width="80" height="50">
      <defs><clipPath id="tip-thumb-titled-b"><rect x="7" y="8" width="66" height="34" rx="4"/></clipPath></defs>
      <g clip-path="url(#tip-thumb-titled-b)"><rect x="7" y="8" width="66" height="14" fill="currentColor"/></g>
      <rect x="7" y="8" width="66" height="34" rx="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
      <circle cx="14" cy="15" r="4" fill="white"/>
      <rect x="22" y="13.5" width="28" height="3" rx="1.5" fill="white"/>
      <line x1="14" y1="29" x2="64" y2="29" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="14" y1="36" x2="56" y2="36" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`,
  },
];

const TIP_ICONS: { label: string; value: string }[] = [
  { label: 'Geen', value: '' },
  { label: 'Lampje', value: 'tip.svg' },
  { label: 'Lostop', value: 'tip-lostop.svg' },
];

const DEFAULTS: TipConfig = { template: 'inline', banner: false, icon: 'tip.svg' };

interface TemplatePicker {
  field: HTMLElement;
  value: () => TipTemplate;
  setValue: (v: TipTemplate) => void;
  setBannerMode: (b: boolean) => void;
  onChange: (cb: (v: TipTemplate) => void) => void;
}

function createTemplatePicker(initial: TipTemplate): TemplatePicker {
  let selected = initial;
  let bannerMode = false;
  const listeners: ((v: TipTemplate) => void)[] = [];
  const buttons = new Map<TipTemplate, HTMLButtonElement>();
  const svgWraps = new Map<TipTemplate, HTMLDivElement>();

  const root = document.createElement('div');
  root.classList.add('flex', 'gap-s', 'flex-wrap');

  const applyActive = () => {
    buttons.forEach((btn, v) => {
      const active = v === selected;
      btn.style.border = active ? '2px solid var(--md-sys-color-primary, #1976d2)' : '2px solid transparent';
      btn.setAttribute('aria-pressed', String(active));
    });
  };

  const applySvgs = () => {
    TEMPLATE_THUMBS.forEach(({ value, svg, svgBanner }) => {
      const wrap = svgWraps.get(value);
      if (wrap) wrap.innerHTML = bannerMode ? svgBanner : svg;
    });
  };

  TEMPLATE_THUMBS.forEach(({ label, value }) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset['template'] = value;
    btn.style.padding = '6px';
    btn.style.borderRadius = '8px';
    btn.style.background = 'transparent';
    btn.style.cursor = 'pointer';
    btn.style.display = 'flex';
    btn.style.flexDirection = 'column';
    btn.style.alignItems = 'center';
    btn.style.gap = '4px';

    const svgWrap = document.createElement('div');
    svgWrap.style.color = 'var(--md-sys-color-on-surface, #1c1b1f)';
    svgWraps.set(value, svgWrap);

    const lbl = document.createElement('div');
    lbl.classList.add('label-small');
    lbl.textContent = label;

    btn.append(svgWrap, lbl);
    btn.addEventListener('click', () => {
      selected = value;
      applyActive();
      listeners.forEach((l) => l(selected));
    });
    buttons.set(value, btn);
    root.append(btn);
  });

  applyActive();
  applySvgs();

  return {
    field: root,
    value: () => selected,
    setValue: (v) => {
      selected = v;
      applyActive();
    },
    setBannerMode: (b) => {
      bannerMode = b;
      applySvgs();
    },
    onChange: (cb) => listeners.push(cb),
  };
}

interface BooleanField {
  field: HTMLElement;
  value: () => boolean;
  setValue: (v: boolean) => void;
  onChange: (cb: (v: boolean) => void) => void;
}

function createSwitch(id: string, label: string, initial: boolean): BooleanField {
  const wrap = document.createElement('label');
  wrap.classList.add('flex', 'items-center', 'gap-s');
  wrap.setAttribute('for', id);

  const sw = document.createElement('dcr-switch');
  sw.setAttribute('id', id);
  if (initial) sw.setAttribute('checked', '');

  const lbl = document.createElement('span');
  lbl.classList.add('label-medium');
  lbl.textContent = label;

  wrap.append(sw, lbl);

  const listeners: ((v: boolean) => void)[] = [];
  sw.addEventListener('change', (e: Event) => {
    const checked = !!(e.target as any)?.checked;
    listeners.forEach((l) => l(checked));
  });

  return {
    field: wrap,
    value: () => !!(sw as any).checked,
    setValue: (v) => {
      (sw as any).checked = v;
    },
    onChange: (cb) => listeners.push(cb),
  };
}

export function tipDialog(
  insertHtml: (html: string) => void,
  iconPath: string,
  mountTarget?: MountTarget,
  existingEl?: HTMLElement
) {
  const dialog = newDialog('Tip invoegen', () => insertHtml(''), mountTarget);

  const template = createTemplatePicker(DEFAULTS.template);
  const banner = createSwitch('banner', 'Banner-header', DEFAULTS.banner);
  const icon = createSelect('icon', 'Icoon', TIP_ICONS);
  icon.setValue(DEFAULTS.icon);

  let content: TipContent = { title: TITLE_PLACEHOLDER, text: TEXT_PLACEHOLDER };

  if (existingEl) {
    const cfg = readConfig<Partial<TipConfig>>(existingEl, DEFAULTS);
    if (cfg.template) template.setValue(cfg.template);
    if (cfg.banner !== undefined) banner.setValue(!!cfg.banner);
    if (cfg.icon !== undefined) icon.setValue(cfg.icon);

    content = extractContent(existingEl);
  }

  banner.onChange((v) => template.setBannerMode(v));
  template.setBannerMode(banner.value());

  dialog.querySelector('#cds-dialog-content').append(template.field, banner.field, icon.field);

  dialog.addEventListener('onSubmit', () => {
    const config: TipConfig = {
      template: template.value(),
      banner: banner.value(),
      icon: icon.value(),
    };
    insertHtml(makeTip(config, iconPath, content));
  });
}

function extractContent(el: HTMLElement): TipContent {
  // Marker class is the durable handle — RTEs (Froala/Learnosity) sometimes strip
  // unrecognized data-* attributes or unwrap classless empty wrappers, but classes
  // survive sanitization. Keep the data-cb-slot attribute as a secondary anchor.
  const titleSlot =
    el.querySelector(`.${SLOT_CLASS_TITLE}`) ?? el.querySelector('[data-cb-slot="title"]');
  const textSlot =
    el.querySelector(`.${SLOT_CLASS_TEXT}`) ?? el.querySelector('[data-cb-slot="text"]');

  // Legacy fallback: pre-slot tips stored title/text inside data-cb-config.
  let legacy: { title?: string; text?: string } = {};
  try {
    legacy = JSON.parse(el.getAttribute('data-cb-config') || '{}');
  } catch {
    legacy = {};
  }

  return {
    title: titleSlot?.innerHTML || legacy.title || TITLE_PLACEHOLDER,
    text: textSlot?.innerHTML || legacy.text || TEXT_PLACEHOLDER,
  };
}

const SLOT_CLASS_TITLE = 'cds-lrn-tip-title';
const SLOT_CLASS_TEXT = 'cds-lrn-tip-text';

const HEADER_PADDING = ['p-i-m', 'p-b-s'];
const BODY_PADDING = ['p-i-m', 'p-b-s'];

function makeTip(config: TipConfig, iconPath: string, content: TipContent): string {
  const wrapper = document.createElement('div');
  wrapper.classList.add('cds-lrn-tip');
  wrapper.setAttribute('data-cb-config', JSON.stringify(config));

  const iconSrc = config.icon ? `${iconPath}${config.icon}` : '';

  if (config.banner) {
    wrapper.append(renderBanner(config, iconSrc, content));
  } else {
    switch (config.template) {
      case 'titled-corner':
        wrapper.append(renderTitledCorner(iconSrc, content));
        break;
      case 'titled':
        wrapper.append(renderTitled(iconSrc, content));
        break;
      case 'inline':
      default:
        wrapper.append(renderInline(iconSrc, content));
        break;
    }
  }

  // Trailing empty paragraph gives Froala/Learnosity a caret landing spot AFTER the block-level
  // tip wrapper. Without it the tip can be the last child of the editable, leaving no way to type
  // content below it.
  return wrapper.outerHTML + '<p><br></p>';
}

function makeSlot(slot: 'title' | 'text', html: string, extraClasses: string[] = []): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('data-cb-slot', slot);
  const markerClass = slot === 'title' ? SLOT_CLASS_TITLE : SLOT_CLASS_TEXT;
  el.classList.add(markerClass, ...extraClasses);
  el.innerHTML = html;
  return el;
}

function renderInline(iconSrc: string, content: TipContent): HTMLElement {
  const container = document.createElement('div');
  container.classList.add(
    'flex',
    'gap-3xs',
    'items-center',
    ...BODY_PADDING,
    'border',
    'border-stroke-variant',
    'corner-m'
  );

  if (iconSrc) container.append(makeIcon(iconSrc));
  container.append(makeSlot('text', content.text, ['headline-medium']));

  return container;
}

function renderTitledCorner(iconSrc: string, content: TipContent): HTMLElement {
  const container = document.createElement('div');
  container.classList.add('relative', ...BODY_PADDING, 'border', 'border-stroke-variant', 'corner-m');

  if (iconSrc) container.append(makeCornerIcon(iconSrc));

  container.append(makeSlot('title', content.title, ['headline-medium']));
  container.append(makeSlot('text', content.text));

  return container;
}

function renderTitled(iconSrc: string, content: TipContent): HTMLElement {
  const container = document.createElement('div');
  container.classList.add(
    'flex',
    'flex-column',
    'gap-3xs',
    ...BODY_PADDING,
    'border',
    'border-stroke-variant',
    'corner-m'
  );

  if (iconSrc) container.append(makeIcon(iconSrc));
  container.append(makeSlot('title', content.title, ['headline-medium']));
  container.append(makeSlot('text', content.text));

  return container;
}

function renderBanner(config: TipConfig, iconSrc: string, content: TipContent): HTMLElement {
  const container = document.createElement('div');
  container.classList.add('border', 'border-stroke-variant', 'corner-m');

  const header = document.createElement('div');
  header.classList.add('secondary-container', 'flex', 'items-center', 'gap-3xs', ...HEADER_PADDING);
  header.style.borderTopLeftRadius = 'inherit';
  header.style.borderTopRightRadius = 'inherit';

  const cornerTemplate = config.template === 'titled-corner';
  if (!cornerTemplate) container.style.overflow = 'hidden';

  let body: HTMLElement | null = null;

  switch (config.template) {
    case 'titled-corner':
      container.classList.add('relative');
      if (iconSrc) container.append(makeCornerIcon(iconSrc, true));
      header.append(makeSlot('title', content.title, ['headline-medium']));
      body = makeSlot('text', content.text, [...BODY_PADDING]);
      break;

    case 'titled':
      if (iconSrc) header.append(makeIcon(iconSrc));
      header.append(makeSlot('title', content.title, ['headline-medium']));
      body = makeSlot('text', content.text, [...BODY_PADDING]);
      break;

    case 'inline':
    default:
      if (iconSrc) header.append(makeIcon(iconSrc));
      header.append(makeSlot('text', content.text, ['headline-medium']));
      break;
  }

  container.append(header);
  if (body) {
    container.append(body);
  } else {
    header.style.borderBottomLeftRadius = 'inherit';
    header.style.borderBottomRightRadius = 'inherit';
  }

  return container;
}

function makeIcon(src: string): HTMLImageElement {
  const icon = document.createElement('img');
  icon.src = src;
  icon.style.height = '32px';
  icon.style.width = '32px';
  return icon;
}

// Froala's image plugin strips `position: absolute` from <img> (it forces fr-fil/fr-dib classes
// and treats images as inline-block draggables). Wrap the img in a span that owns the absolute
// positioning so the layout survives a round-trip through Froala.
function makeCornerIcon(src: string, raiseAboveHeader = false): HTMLElement {
  const wrap = document.createElement('span');
  wrap.style.position = 'absolute';
  wrap.style.top = '-12px';
  wrap.style.left = '-12px';
  if (raiseAboveHeader) wrap.style.zIndex = '1';
  wrap.append(makeIcon(src));
  return wrap;
}
